<script setup>
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inboxId: { type: Number, required: true },
});

const emit = defineEmits(['imported']);

const EXTENSION_URL =
  'https://chromewebstore.google.com/detail/passkey-linker/hehoacnepmncbjckgnfekfcgdijpigaj';

const { t } = useI18n();

const isExpanded = ref(false);
const isSubmitting = ref(false);
const showSuccessNote = ref(false);

const state = reactive({ creds: '' });

const isValidJson = value => {
  if (!helpers.req(value)) return true;

  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    return false;
  }
};

const rules = {
  creds: {
    required: helpers.withMessage(
      () => t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.ERROR_REQUIRED'),
      required
    ),
    validJson: helpers.withMessage(
      () => t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.ERROR_INVALID_JSON'),
      isValidJson
    ),
  },
};

const v$ = useVuelidate(rules, state);

const credsError = computed(() =>
  v$.value.creds.$error ? v$.value.creds.$errors[0]?.$message : ''
);

const submitLabel = computed(() =>
  isSubmitting.value
    ? t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.SUBMITTING')
    : t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.SUBMIT')
);

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  isSubmitting.value = true;

  try {
    await InboxesAPI.baileysImportCreds(props.inboxId, JSON.parse(state.creds));

    useAlert(t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.SUCCESS'));
    state.creds = '';
    v$.value.$reset();
    isExpanded.value = false;
    showSuccessNote.value = true;
    emit('imported');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.ERROR_GENERIC')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="w-full text-left">
    <NextButton
      link
      slate
      size="sm"
      trailing-icon
      :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
      :label="t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.TRIGGER')"
      @click="isExpanded = !isExpanded"
    />

    <div
      v-if="showSuccessNote"
      class="flex items-start gap-2 p-3 mt-3 border rounded-lg border-n-amber-6 bg-n-amber-3"
    >
      <span
        class="i-lucide-triangle-alert size-4 shrink-0 text-n-amber-11 mt-0.5"
      />
      <p class="text-xs text-n-amber-11">
        {{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.SUCCESS_NOTE') }}
      </p>
    </div>

    <div
      v-if="isExpanded"
      class="flex flex-col gap-4 p-4 mt-3 border rounded-lg border-n-weak bg-n-slate-2"
    >
      <div class="flex flex-col gap-1">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.WHY') }}
        </p>
      </div>

      <ol class="space-y-1 text-xs list-decimal list-inside text-n-slate-11">
        <li>{{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.STEP_1') }}</li>
        <li>{{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.STEP_2') }}</li>
        <li>{{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.STEP_3') }}</li>
        <li class="font-medium text-n-amber-11">
          {{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.STEP_4') }}
        </li>
        <li>{{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.STEP_5') }}</li>
      </ol>

      <a
        :href="EXTENSION_URL"
        target="_blank"
        rel="noopener noreferrer"
        class="text-xs font-medium text-n-blue-11 hover:underline"
      >
        {{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.EXTENSION_LINK') }}
      </a>

      <div class="p-3 border rounded-lg border-n-amber-6 bg-n-amber-3">
        <p class="text-xs text-n-amber-11">
          {{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.WARNING') }}
        </p>
      </div>

      <form class="flex flex-col gap-2" @submit.prevent="handleSubmit">
        <label
          for="baileys-creds-import"
          class="text-sm font-medium text-n-slate-12"
        >
          {{ t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.CREDS_LABEL') }}
        </label>
        <textarea
          id="baileys-creds-import"
          v-model="state.creds"
          rows="6"
          :placeholder="t('INBOX_MGMT.BAILEYS.CREDS_IMPORT.CREDS_PLACEHOLDER')"
          class="w-full p-3 font-mono text-xs border rounded-lg resize-none reset-base !mb-0 bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
          :class="
            credsError
              ? 'border-n-ruby-8 focus:border-n-ruby-9'
              : 'border-n-weak focus:border-n-brand-ink'
          "
          @blur="v$.creds.$touch()"
        />
        <p v-if="credsError" class="text-xs text-n-ruby-11">
          {{ credsError }}
        </p>

        <div class="flex justify-end">
          <NextButton
            type="submit"
            solid
            blue
            size="sm"
            :is-loading="isSubmitting"
            :disabled="isSubmitting"
            :label="submitLabel"
          />
        </div>
      </form>
    </div>
  </div>
</template>
